import axios, { type AxiosError, type AxiosInstance } from 'axios';
import type {
  AuthTokens,
  LoginRequest,
  RegisterRequest,
  User,
  Tenant,
  Expense,
  CreateExpenseRequest,
  ExpenseCategory,
  Loan,
  CreateLoanRequest,
  LoanPayment,
  Tanda,
  CreateTandaRequest,
  RecurringService,
  CreateRecurringServiceRequest,
  Call,
  CallQueueStats,
  DashboardSummary,
  ApiError,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

class ApiService {
  private client: AxiosInstance;
  private refreshPromise: Promise<string> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && originalRequest) {
          try {
            const newToken = await this.refreshToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch {
            this.logout();
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async login(data: LoginRequest): Promise<AuthTokens> {
    const response = await this.client.post<{ success: boolean; data: AuthTokens }>('/auth/login', data);
    const tokens = response.data.data;
    this.setTokens(tokens);
    return tokens;
  }

  async register(data: RegisterRequest): Promise<AuthTokens> {
    const response = await this.client.post<{ success: boolean; data: AuthTokens }>('/auth/register', data);
    const tokens = response.data.data;
    this.setTokens(tokens);
    return tokens;
  }

  async refreshToken(): Promise<string> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    this.refreshPromise = this.client
      .post<{ success: boolean; data: { access_token: string } }>('/auth/refresh', { refresh_token: refreshToken })
      .then((response) => {
        const newToken = response.data.data.access_token;
        localStorage.setItem('access_token', newToken);
        return newToken;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  async getProfile(): Promise<{ user: User; tenant: Tenant }> {
    const response = await this.client.get<{ success: boolean; data: User }>('/auth/me');
    // Backend returns user directly, wrap it for compatibility
    return { user: response.data.data, tenant: {} as Tenant };
  }

  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  private setTokens(tokens: AuthTokens): void {
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  // Dashboard
  async getDashboard(): Promise<DashboardSummary> {
    const response = await this.client.get('/dashboard');
    return response.data;
  }

  async getCallQueueStats(): Promise<CallQueueStats> {
    const response = await this.client.get('/dashboard/call-queue');
    return response.data;
  }

  // Expenses
  async getExpenses(limit = 50): Promise<Expense[]> {
    const response = await this.client.get(`/expenses?limit=${limit}`);
    return response.data;
  }

  async createExpense(data: CreateExpenseRequest): Promise<Expense> {
    const response = await this.client.post('/expenses', data);
    return response.data;
  }

  async getExpenseCategories(): Promise<ExpenseCategory[]> {
    const response = await this.client.get('/expenses/categories');
    return response.data;
  }

  async createExpenseCategory(data: { name: string; icon: string; monthly_budget: number }): Promise<ExpenseCategory> {
    const response = await this.client.post('/expenses/categories', data);
    return response.data;
  }

  // Loans
  async getLoans(): Promise<Loan[]> {
    const response = await this.client.get('/loans');
    return response.data;
  }

  async getLoan(id: string): Promise<Loan> {
    const response = await this.client.get(`/loans/${id}`);
    return response.data;
  }

  async createLoan(data: CreateLoanRequest): Promise<Loan> {
    const response = await this.client.post('/loans', data);
    return response.data;
  }

  async recordLoanPayment(
    loanId: string,
    data: { amount: number; payment_method?: string; notes?: string }
  ): Promise<LoanPayment> {
    const response = await this.client.post(`/loans/${loanId}/payments`, data);
    return response.data;
  }

  // Tandas
  async getTandas(): Promise<Tanda[]> {
    const response = await this.client.get('/tandas');
    return response.data;
  }

  async createTanda(data: CreateTandaRequest): Promise<Tanda> {
    const response = await this.client.post('/tandas', data);
    return response.data;
  }

  // Recurring Services
  async getRecurringServices(): Promise<RecurringService[]> {
    const response = await this.client.get('/services');
    return response.data;
  }

  async createRecurringService(data: CreateRecurringServiceRequest): Promise<RecurringService> {
    const response = await this.client.post('/services', data);
    return response.data;
  }

  // Calls
  async getCallLogs(limit = 50): Promise<Call[]> {
    const response = await this.client.get(`/calls?limit=${limit}`);
    return response.data;
  }

  async queueCall(data: { phone_number: string; call_type: string }): Promise<void> {
    await this.client.post('/calls', data);
  }

  async processCallQueue(): Promise<{ processed: number }> {
    const response = await this.client.post('/calls/process-queue');
    return response.data;
  }
}

export const api = new ApiService();
export default api;
